/** Small formatting helpers for the phone views. */

const isArabic = (lang: string) => (lang || '').toLowerCase().startsWith('ar');

/** "now", "5m", "3h", "2d", then a short date — the way phone lists show age. */
export function timeAgoShort(iso: string | Date | null | undefined, lang: string): string {
  if (!iso) return '';
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  const ar = isArabic(lang);
  if (mins < 1) return ar ? 'الآن' : 'now';
  if (mins < 60) return ar ? `${mins} د` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return ar ? `${hours} س` : `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return ar ? `${days} ي` : `${days}d`;
  return date.toLocaleDateString(ar ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-GB', { day: 'numeric', month: 'short' });
}

/** 1234.5 → "1,234.50" (Latin digits, as on the invoices). */
export function money(n: number | null | undefined): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  return v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
}

/** Status → the phone colour class (each status its own hue: the desktop badges reuse amber
 *  for PENDING and INVOICE_REQUEST and green for READY and DELIVERED). */
export function statusTone(status: string | null | undefined): string {
  switch (status) {
    case 'PENDING':
    case 'NOT_QUOTED':
      return 'm-st-pending';
    case 'QUOTED':
      return 'm-st-quoted';
    case 'PR':
    case 'PURCHASE_REQUEST':
      return 'm-st-pr';
    case 'READY_FOR_PICKUP':
      return 'm-st-ready';
    case 'INVOICE_REQUEST':
    case 'INVOICED':
      return 'm-st-invoice';
    case 'DELIVERED':
      return 'm-st-delivered';
    case 'CANCELED':
      return 'm-st-canceled';
    default:
      return 'm-st-neutral';
  }
}

/* ── Workshop v2 (src/workshop): the one money / number formatter ─────────────────────────── */

export type Digits = 'arab' | 'latn';

/** A number for the workshop pages: Western digits («1,840») unless `digits: 'arab'` is asked for. */
export function fmtNum(n: number | null | undefined, lang: string, opts: { digits?: Digits; minFraction?: number; maxFraction?: number } = {}): string {
  const v = typeof n === 'number' && isFinite(n) ? n : 0;
  // Western digits by default in both languages, like the rest of Mize Pro; «arab» only when asked for.
  const digits: Digits = opts.digits ?? 'latn';
  const locale = digits === 'arab' ? 'ar-SA-u-nu-arab' : 'en-US';
  return v.toLocaleString(locale, { minimumFractionDigits: opts.minFraction ?? 0, maximumFractionDigits: opts.maxFraction ?? 2 });
}

/** Saudi riyals: «1,840 ر.س» (Arabic) / «SAR 1,840» (English). Halalas, as two digits, only when there are any. */
export function sar(n: number | null | undefined, lang: string, opts: { digits?: Digits } = {}): string {
  const v = typeof n === 'number' && isFinite(n) ? Math.round(n * 100) / 100 : 0;
  const halalas = !Number.isInteger(v);
  const num = fmtNum(v, lang, { digits: opts.digits, minFraction: halalas ? 2 : 0, maxFraction: halalas ? 2 : 0 });
  return isArabic(lang) ? `${num} ر.س` : `SAR ${num}`;
}
